import { useEffect, useRef, useState } from "react";
import { AlertTriangle, BarChart3, Loader2, Paperclip, Send } from "@fg/icons";
import { type ConversationScopeKind } from "@/api/connectApi";
import { PollComposerDialog } from "../polls/PollComposerDialog";
import { EmojiPickerTrigger } from "../EmojiPicker";
import { draftToHtml } from "@/lib/richHtml";
import { readDraft, writeDraft } from "@/lib/drafts";
import { useSettings } from "@/lib/settings";
import { wrapImportance, type Importance } from "@/lib/importance";
import type { DriveShareSource } from "@/lib/drive";
import { cn } from "@/lib/utils";
import { ComposerAttachments, DropOverlay } from "../files/ComposerAttachments";
import { filesFromPaste, useFileDropZone, type DriveAttachments } from "../files/useDriveAttachments";
import { RichInput } from "../rich/RichInput";
import type { EditorState, MentionCandidate, RichEditorHandle } from "../rich/RichEditor";

export type { MentionCandidate } from "../rich/RichEditor";

/** Who a send shares its files with — resolved at send time so a late-loading member list is still used. */
export interface ChatShareTarget {
    emails: string[];
    ownerEmail: string;
    source: DriveShareSource;
    sourceRef: string;
}

/**
 * Composer for 1:1 and group chats, channel replies, threads and meeting chat: the Teams-style rich
 * card (features/rich) with a formatting toolbar above the text, an "Aa" button that hides it, and
 * emoji, attach, poll and delivery options in the bottom row. Bold, italic, underline, strikethrough,
 * lists, colour, highlight, size, quote, links and code go out as the HTML contract of lib/richHtml.ts;
 * **bold**, _italic_ and `code` typed as Markdown still become formatting when the message is sent.
 * Enter sends (Shift+Enter for a new line) unless Settings > General says Ctrl+Enter.
 *
 * Files (attach button, paste, or drop) upload to the sender's Drive as soon as they're attached and are
 * shared with the chat on send. The box clears the moment you press Send so you can keep typing while
 * files finish uploading; if the send fails the text and files come back.
 *
 * Typing "@" opens a people picker (the chat's members first, then the directory); a pick becomes a
 * mention chip and the person, if they're in the chat, gets an Activity notification and an "@" badge.
 */
export function ChatComposer({
    placeholder, disabled, onSend, onTyping, attachments, resolveShare, mentionCandidates, poll, requireRecipients = true, draftKey, onEditLast, everyone, importanceFlags
}: {
    placeholder: string;
    disabled?: boolean;
    onSend: (html: string) => Promise<void>;
    onTyping?: (started: boolean) => void;
    attachments?: DriveAttachments;
    resolveShare?: () => Promise<ChatShareTarget>;
    /** The chat's other members; enables @mentions. */
    mentionCandidates?: MentionCandidate[];
    /** Where a poll created from the composer's Poll button belongs; no button without it. */
    poll?: { scope: ConversationScopeKind; conversationId: string };
    /** Refuse to send files when there's nobody else to share them with (chats); a channel may be just you. */
    requireRecipients?: boolean;
    /** Keeps the unsent text under this key (a chat list key) so it survives switching chats and reloads. */
    draftKey?: string;
    /** Up arrow in an empty box: open your newest message for editing. */
    onEditLast?: () => void;
    /** Offer "@everyone" in the mention list (group chats). */
    everyone?: boolean;
    /** Show the Important / Urgent choice (chats). */
    importanceFlags?: boolean;
}) {
    const { enterToSend } = useSettings();
    const editor = useRef<RichEditorHandle>(null);
    const [pollOpen, setPollOpen] = useState(false);
    const [initialHtml] = useState(() => (draftKey ? draftToHtml(readDraft(draftKey)) : ""));
    const [empty, setEmpty] = useState(() => !initialHtml);
    const [importance, setImportance] = useState<Importance>("standard");
    const [importanceOpen, setImportanceOpen] = useState(false);
    // Save the draft (as HTML) shortly after typing stops; leaving the chat flushes it (the composer remounts per chat).
    const [draftHtml, setDraftHtml] = useState(initialHtml);
    const draftRef = useRef(draftHtml);
    draftRef.current = draftHtml;
    useEffect(() => {
        if (!draftKey) return;
        const t = window.setTimeout(() => writeDraft(draftKey, draftHtml), 400);
        return () => window.clearTimeout(t);
    }, [draftKey, draftHtml]);
    useEffect(() => () => { if (draftKey) writeDraft(draftKey, draftRef.current); }, [draftKey]);
    const [inFlight, setInFlight] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const typingRef = useRef(false);
    const idleTimer = useRef<number | undefined>(undefined);
    const onTypingRef = useRef(onTyping);
    onTypingRef.current = onTyping;
    const drop = useFileDropZone((files) => attachments?.addFiles(files));
    const mentionsOn = Boolean(mentionCandidates);

    // "Reply with a quote" on a message in this chat puts it above what you type.
    useEffect(() => {
        if (!draftKey) return;
        const onQuote = (e: Event) => {
            const d = (e as CustomEvent<{ containerId: string; author: string; text: string }>).detail;
            if (!d || !draftKey.endsWith(`:${d.containerId}`)) return;
            editor.current?.insertQuote(d.author, d.text);
        };
        window.addEventListener("fg:quote-message", onQuote);
        return () => window.removeEventListener("fg:quote-message", onQuote);
    }, [draftKey]);

    const stopTyping = () => {
        if (idleTimer.current !== undefined) window.clearTimeout(idleTimer.current);
        if (typingRef.current) {
            typingRef.current = false;
            onTypingRef.current?.(false);
        }
    };

    useEffect(() => () => stopTyping(), []);

    const onChange = (state: EditorState) => {
        setEmpty(state.empty);
        setDraftHtml(state.html);
        setError(null);
        if (!onTypingRef.current) return;
        if (!typingRef.current && !state.empty) {
            typingRef.current = true;
            onTypingRef.current(true);
        }
        if (idleTimer.current !== undefined) window.clearTimeout(idleTimer.current);
        idleTimer.current = window.setTimeout(stopTyping, 3000);
    };

    const pendingFiles = attachments?.files.filter((f) => !f.locked) ?? [];

    const send = async () => {
        const ed = editor.current;
        if (!ed) return;
        const html = ed.getHtml();
        if ((!html && pendingFiles.length === 0) || disabled) return;
        const sentImportance = importance;
        ed.clear();
        setEmpty(true);
        setDraftHtml("");
        setImportance("standard");
        if (draftKey) writeDraft(draftKey, "");
        stopTyping();
        setError(null);
        setInFlight((n) => n + 1);
        let keys: string[] = [];
        try {
            let markers = "";
            if (attachments && pendingFiles.length > 0) {
                if (!resolveShare) throw new Error("Files can't be shared here.");
                const share = await resolveShare();
                const prepared = await attachments.prepareSend({ ...share, requireRecipients });
                markers = prepared.markersHtml;
                keys = prepared.keys;
            }
            await onSend(wrapImportance(html + markers, sentImportance));
            if (keys.length > 0) attachments?.commit(keys);
            ed.focus();
        } catch (err) {
            if (keys.length > 0) attachments?.release(keys);
            // Put the text (and its mentions) back unless they've already started a new message.
            if (editor.current?.isEmpty() && html) {
                editor.current.setHtml(html);
                setImportance(sentImportance);
            }
            const msg = err instanceof Error ? err.message : "";
            setError(msg && !msg.startsWith("Connect BFF")
                ? msg
                : "Couldn't send — your message is still here. Try again.");
        } finally {
            setInFlight((n) => n - 1);
        }
    };

    const iconButton = "flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40";
    const canSend = (!empty || pendingFiles.length > 0) && !disabled;

    return (
        <div className="relative shrink-0 px-3 pb-3 pt-1 sm:px-4 sm:pb-3.5" {...(attachments ? drop.dropProps : {})}>
            {attachments && drop.isDragging ? <DropOverlay /> : null}
            <RichInput
                ref={editor}
                initialHtml={initialHtml}
                placeholder={placeholder}
                disabled={disabled}
                mentionCandidates={mentionCandidates}
                everyone={everyone}
                onChange={onChange}
                onSubmit={() => void send()}
                onEditLast={onEditLast}
                onPaste={(e) => {
                    if (!attachments) return false;
                    const pasted = filesFromPaste(e);
                    if (pasted.length === 0) return false;
                    e.preventDefault();
                    attachments.addFiles(pasted);
                    return true;
                }}
                onDiscard={() => {
                    setEmpty(true);
                    setDraftHtml("");
                    setError(null);
                    if (draftKey) writeDraft(draftKey, "");
                    stopTyping();
                }}
                header={attachments && attachments.files.length > 0 ? (
                    <div className="px-3 pb-1 pt-2">
                        <ComposerAttachments attachments={attachments} />
                    </div>
                ) : null}
                footerLeft={(
                    <>
                        <EmojiPickerTrigger onSelect={(emoji) => editor.current?.insertText(emoji)} />
                        {attachments ? (
                            <>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    hidden
                                    onChange={(e) => {
                                        if (e.target.files && e.target.files.length > 0) attachments.addFiles(e.target.files);
                                        e.target.value = "";
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={disabled}
                                    aria-label="Attach files"
                                    title="Attach files"
                                    className={iconButton}
                                >
                                    <Paperclip className="h-4 w-4" />
                                </button>
                            </>
                        ) : null}
                        {importanceFlags ? (
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => setImportanceOpen((o) => !o)}
                                    disabled={disabled}
                                    aria-label="Set delivery options"
                                    aria-expanded={importanceOpen}
                                    title="Delivery options (Important, Urgent)"
                                    className={cn(
                                        "flex h-8 w-8 shrink-0 items-center justify-center rounded-md hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40",
                                        importance === "urgent" ? "text-destructive" : importance === "important" ? "text-amber-500" : "text-muted-foreground hover:text-foreground"
                                    )}
                                >
                                    <AlertTriangle className="h-4 w-4" />
                                </button>
                                {importanceOpen ? (
                                    <div role="menu" className="absolute bottom-full left-0 z-30 mb-1 w-56 rounded-md border bg-popover p-1 text-sm shadow-lg">
                                        {([["standard", "Standard", "Delivered as usual"], ["important", "Important", "Marked with an Important label"], ["urgent", "Urgent", "Marked Urgent so it stands out"]] as const).map(([v, label, hint]) => (
                                            <button
                                                key={v}
                                                type="button"
                                                role="menuitemradio"
                                                aria-checked={importance === v}
                                                onClick={() => { setImportance(v); setImportanceOpen(false); editor.current?.focus(); }}
                                                className={cn("flex w-full flex-col rounded-sm px-2 py-1.5 text-left hover:bg-accent", importance === v && "bg-accent")}
                                            >
                                                <span className="font-medium">{label}</span>
                                                <span className="text-[11px] text-muted-foreground">{hint}</span>
                                            </button>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                        {poll ? (
                            <button
                                type="button"
                                onClick={() => setPollOpen(true)}
                                disabled={disabled}
                                aria-label="Create a poll"
                                title="Poll"
                                className={iconButton}
                            >
                                <BarChart3 className="h-4 w-4" />
                            </button>
                        ) : null}
                    </>
                )}
                footerRight={(
                    <button
                        type="button"
                        onClick={() => void send()}
                        disabled={!canSend}
                        aria-label="Send"
                        title="Send (Enter)"
                        className={cn(
                            "flex h-8 w-8 shrink-0 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1",
                            canSend
                                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                                : "text-muted-foreground opacity-60"
                        )}
                    >
                        {inFlight > 0 ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </button>
                )}
            />
            {error ? (
                <p role="alert" className="mt-1 px-1 text-xs text-destructive">{error}</p>
            ) : importance !== "standard" ? (
                <p className="mt-1 px-1 text-[11px] font-medium text-muted-foreground">Sending as {importance === "urgent" ? "Urgent" : "Important"} · <button type="button" className="underline" onClick={() => setImportance("standard")}>Remove</button></p>
            ) : (
                <p className="mt-1 hidden px-1 text-[10px] text-muted-foreground sm:block">
                    {enterToSend ? "Enter to send · Shift+Enter for a new line" : "Ctrl+Enter to send · Enter for a new line"}{mentionsOn ? " · @ to mention" : ""} · **bold** _italic_ `code`{attachments ? " · paste or drop files to share" : ""}
                </p>
            )}
            {poll ? (
                <PollComposerDialog
                    open={pollOpen}
                    onOpenChange={setPollOpen}
                    scope={poll.scope}
                    conversationId={poll.conversationId}
                    onPost={(html) => onSend(html)}
                />
            ) : null}
        </div>
    );
}
